import { getActiveAccount } from "./AuthorizationUtils";
import { AppRoles } from './AuthorizationConfig';
import { msalInit } from "./AuthorizationProvider";

interface TokenClaims {
    name?: string;
    preferred_username?: string;
    email?: string;
    upn?: string;
    roles?: string[];
}

export class UserInformation {
    constructor(
        public readonly name: string,
        public readonly email: string,
        public readonly role: string
    ) {}

    static async fromTokenClaims(): Promise<UserInformation | null> {
        const claims = await getTokenClaims();
        if (!claims) return null;

        const name = claims.name || claims.preferred_username || 'Unknown User';      
        const email = claims.email || claims.upn || claims.preferred_username || 'Unknown Email';
        
        // More flexible role extraction
        const roles = claims.roles;
        var role = null;

        // Add prioritization logic for roles
        if (roles) {
            if (roles.includes(AppRoles.ADMIN)) {
                role = AppRoles.ADMIN;
            } 
            else if (roles.includes(AppRoles.CUSTOMER)) {
                role = AppRoles.CUSTOMER;
            } else {
                role = roles[0]; // Fallback to the first role
            }
        }

        if (!role) {
            console.warn("No valid role found in token claims");
            return null;
        }

        return new UserInformation(
            String(name),
            String(email),
            String(role)
        );
    }
}


const getTokenClaims = async (): Promise<TokenClaims | null> => {
    try {
        // Ensure MSAL is initialized
        await msalInit;
        
        const account = await getActiveAccount();
        if (!account) {
            throw new Error("No active account found");
        }

        if (!account.idTokenClaims) {
            throw new Error("No ID token claims present in account");
        }

        const claims = account.idTokenClaims as TokenClaims;
        
        // Log full claims for debugging
        console.debug('Token claims:', JSON.stringify(claims, null, 2));
        
        return claims;
    } catch (error) {
        console.error("Error getting token claims:", error);
        throw error; // Propagate error for better handling
    }
};

export const getUserName = async (): Promise<string | null> => {
    const userInfo = await UserInformation.fromTokenClaims();
    return userInfo?.name ?? null;
};

export const getEmail = async (): Promise<string | null> => {
    const userInfo = await UserInformation.fromTokenClaims();
    return userInfo?.email ?? null;
};

export const getRole = async (): Promise<string | null> => {
    const userInfo = await UserInformation.fromTokenClaims();
    return userInfo?.role ?? null;
};

// Direct role checking functions
export const checkIsAdmin = async (): Promise<boolean> => {
    const role = await getRole();
    return role === AppRoles.ADMIN;
};

export const checkIsCustomer = async (): Promise<boolean> => {
    const role = await getRole();
    return role === AppRoles.CUSTOMER;
};